import { useRef } from 'react'

import { Button, Drawer, Tag } from 'antd'
import { isNil } from 'lodash'

import { queryBidRecordListApi } from '@eastcoal/api/trade/bidRecordOperation/queryBidRecordList'
import {
  AuctionOperationPageItem,
  BidRecordEntity
} from '@eastcoal/api/trade/interface'

import { RemoteTable } from '@eastcoal/common/components/RemoteTable'
import { RemoteTableStoreRef } from '@eastcoal/common/components/RemoteTable/Store/interface'
import { pretty } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'

import {
  BidPriceType,
  BidPriceTypeTextMap
} from '@eastcoal/shared/enums/BidPriceType'
import {
  BidRecordStatus,
  BidRecordStatusColorMap,
  BidRecordStatusPlatformTextMap
} from '@eastcoal/shared/enums/BidRecordStatus'
import { getSeatNo } from '@eastcoal/shared/utils/auction'

import { AuctionOperationPageStatus } from '../../../enums/AuctionOperationPageStatus'
import { useAuctionListStore } from '../../../Store/hooks/useStore'
import { BidRecordConfirmButton } from '../BidRecordConfirmButton'

import { BidRecordTableProps } from './interface'

const renderPrice = (
  value: any,
  _: BidRecordEntity,
  __: number,
  context: AuctionOperationPageItem
) => {
  const { bidPriceType, priceUnit = '-' } = context ?? {}
  const desc =
    bidPriceType === BidPriceType.TOTAL_PRICE
      ? `元(${BidPriceTypeTextMap[bidPriceType as BidPriceType]})`
      : priceUnit +
        (isNil(bidPriceType)
          ? ''
          : ` (${BidPriceTypeTextMap[bidPriceType as BidPriceType]})`)

  return isNil(value) ? (
    '-'
  ) : (
    <>
      <span style={{ fontWeight: 'bold' }}>{pretty(value)}</span>
      <span>{desc}</span>
    </>
  )
}

const renderQuantity = (
  value: any,
  _: BidRecordEntity,
  __: number,
  context: AuctionOperationPageItem
) => {
  const { quantityUnit } = context ?? {}
  return isNil(value) ? (
    '-'
  ) : (
    <>
      <span style={{ fontWeight: 'bold' }}>{pretty(value)}</span>
      <span>{quantityUnit}</span>
    </>
  )
}

const columns: BidRecordTableProps['columns'] = [
  {
    dataIndex: 'applyId',
    title: '席位号',
    width: 100,
    render: getSeatNo
  },
  {
    dataIndex: 'memberName',
    title: '公司名'
  },
  {
    dataIndex: 'profileName',
    title: '参与人员',
    align: 'center'
  },
  {
    dataIndex: 'price',
    title: '报价',
    align: 'right',
    render: renderPrice
  },
  {
    dataIndex: 'quantity',
    title: '数量',
    align: 'right',
    render: renderQuantity
  },
  {
    dataIndex: 'dealPrice',
    title: '成交价格',
    align: 'right',
    render: renderPrice
  },
  {
    dataIndex: 'dealQuantity',
    title: '成交数量',
    align: 'right',
    render: renderQuantity
  },
  {
    dataIndex: 'bidTime',
    title: '时间',
    align: 'center'
  },
  {
    dataIndex: 'loginIp',
    title: 'IP地址',
    align: 'center',
    render(value: string) {
      return isNil(value) ? null : <Tag>{value.split(',')[0]}</Tag>
    }
  },
  {
    dataIndex: 'status',
    title: '状态',
    align: 'center',
    render(value: BidRecordStatus) {
      return (
        <Tag color={BidRecordStatusColorMap[value]} variant='filled'>
          {value === BidRecordStatus.WAIT_ENTRUST_CONFIRM
            ? '出价'
            : BidRecordStatusPlatformTextMap[value]}
        </Tag>
      )
    }
  },
  {
    dataIndex: 'action',
    title: '操作',
    align: 'center',
    visible(context) {
      return context.status === AuctionOperationPageStatus.ENTRUST_CONFIRMED
    },
    render(_, record) {
      return <BidRecordConfirmButton record={record} />
    }
  }
]

export const BidRecordListModal = () => {
  const {
    currentAuction,
    bidRecordListModalVisible,
    toggleBidRecordListModalVisible
  } = useAuctionListStore(state =>
    pick(state, [
      'currentAuction',
      'bidRecordListModalVisible',
      'toggleBidRecordListModalVisible'
    ])
  )

  const tableRef = useRef<RemoteTableStoreRef>(null)

  const { id, title } = currentAuction || {}

  const handleDrawerClose = () => {
    toggleBidRecordListModalVisible(false)
  }

  const handleRefreshRecord = () => {
    tableRef.current?.reload()
  }

  return (
    <Drawer
      open={bidRecordListModalVisible}
      title={`${title}-出价记录`}
      extra={
        <Button type='default' onClick={handleRefreshRecord}>
          刷新记录
        </Button>
      }
      styles={{
        wrapper: {
          width: '90%'
        }
      }}
      onClose={handleDrawerClose}
    >
      <RemoteTable
        ref={tableRef}
        api={queryBidRecordListApi}
        columns={columns}
        context={currentAuction}
        pagination={false}
        readyRequest={bidRecordListModalVisible && !!id}
        refreshDeps={[bidRecordListModalVisible, id]}
        rowKey='id'
        requestParams={{
          id
        }}
      />
    </Drawer>
  )
}
